import { Download } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadBlob, downloadText, exportEmployeeTouchDetail } from "@/lib/api";
import { ReportMultiSelect, appendRepeatQuery } from "@/pages/dashboard/admin/ReportMultiSelect";
import { useTouchDetailExportViewModel } from "@/pages/dashboard/admin/useTouchDetailExportViewModel";

export function TouchDetailExportView() {
  const {
    isAdmin,
    payrollFormat, setPayrollFormat,
    payrollCoCode, setPayrollCoCode,
    payrollOfficeLocation, setPayrollOfficeLocation,
    payrollExportError, setPayrollExportError,
    payrollPayWeek, setPayrollPayWeek,
    fromDate, setFromDate,
    toDate, setToDate,
    payrollCoCodesData, payrollCoCodesLoading, payrollCoCodesError,
    officeLocationsData, officeLocationsLoading, officeLocationsError,
    payWeeksData, payWeeksLoading, payWeeksError,
  } = useTouchDetailExportViewModel();
  return (
    <>
                {/* New: Employee Touch Detail export (per pay week) - admin only */}
                {isAdmin && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex flex-wrap items-center gap-2">
                        Employee Touch Detail (Pay Week)
                        <Badge variant="outline">Authoritative for punch audit</Badge>
                      </CardTitle>
                      <CardDescription>
                        Punch-level audit (times, sites, cost codes) — not the ADP import file.
                        Use Pay Week for naming; IST for India offices; HKT for Hong Kong.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-wrap items-end gap-3">
                      <ReportMultiSelect
                        label="Co Code"
                        allLabel="All co codes"
                        options={payrollCoCodesData?.co_codes ?? []}
                        optionLabels={payrollCoCodesData?.companies}
                        className="min-w-[220px]"
                        value={payrollCoCode}
                        onChange={setPayrollCoCode}
                        loading={payrollCoCodesLoading}
                        error={!!payrollCoCodesError}
                      />

                      <ReportMultiSelect
                        label="Office Location"
                        allLabel="All offices"
                        options={officeLocationsData?.office_locations ?? []}
                        value={payrollOfficeLocation}
                        onChange={setPayrollOfficeLocation}
                        loading={officeLocationsLoading}
                        error={!!officeLocationsError}
                        className="min-w-[200px]"
                      />

                      {/* Pay Week dropdown (same data source) - convenience + naming for touch detail export */}
                      <div className="grid gap-1 min-w-[220px]">
                        <Label className="text-xs text-muted-foreground">Pay Week</Label>
                        <Select
                          value={
                            (payWeeksData?.pay_weeks ?? []).find(
                              (x) => x.from_date === fromDate && x.pay_week === payrollPayWeek,
                            )?.from_date
                            || (payWeeksData?.pay_weeks ?? []).find((x) => x.from_date === fromDate)?.from_date
                            || "custom"
                          }
                          onValueChange={(v) => {
                            if (v === "custom") {
                              setPayrollPayWeek("");
                            } else {
                              const w = (payWeeksData?.pay_weeks ?? []).find((x) => x.from_date === v);
                              if (w) {
                                setPayrollPayWeek(w.pay_week);
                                setFromDate(w.from_date);
                                setToDate(w.to_date);
                              }
                            }
                          }}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Select pay week or custom dates" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Payroll Week</SelectItem>
                            {(payWeeksData?.pay_weeks ?? []).map((w) => (
                              <SelectItem key={w.from_date} value={w.from_date}>{w.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {payWeeksLoading && <div className="text-[10px] text-muted-foreground">Loading pay weeks...</div>}
                        {payWeeksError && <div className="text-[10px] text-destructive">Failed to load pay weeks</div>}
                      </div>

                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">From</Label>
                        <Input type="date" value={fromDate || ""} onChange={(e) => setFromDate(e.target.value)} className="w-[150px]" />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs text-muted-foreground">To</Label>
                        <Input type="date" value={toDate || ""} onChange={(e) => setToDate(e.target.value)} className="w-[150px]" />
                      </div>
                      <div className="grid gap-1 min-w-[120px]">
                        <Label className="text-xs text-muted-foreground">Format</Label>
                        <Select value={payrollFormat} onValueChange={(v) => setPayrollFormat(v as any)}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="xlsx">XLSX</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button type="button" onClick={async () => {
                        setPayrollExportError(null);
                        try {
                          const params = new URLSearchParams();
                          if (fromDate) params.set("from_date", fromDate);
                          if (toDate) params.set("to_date", toDate);
                          appendRepeatQuery(params, "co_code", payrollCoCode);
                          appendRepeatQuery(params, "office_location", payrollOfficeLocation);
                          if (payrollPayWeek) params.set("pay_week", payrollPayWeek);
                          const result = await exportEmployeeTouchDetail(params, payrollFormat);
                          // Prefer pay week for name when present (server will also use the correct one).
                          const base = payrollPayWeek
                            ? `EmployeeTouchDetail (${payrollPayWeek})`
                            : `EmployeeTouchDetail_${payrollCoCode.join("-") || "all"}`;
                          if (payrollFormat === "xlsx") {
                            downloadBlob(`${base}.xlsx`, result as Blob);
                          } else {
                            downloadText(`${base}.csv`, result as string, "text/csv;charset=utf-8");
                          }
                        } catch (e: any) {
                          setPayrollExportError(e?.message || "Export failed");
                        }
                      }} disabled={!isAdmin}>
                        <Download className="size-4" /> Export Touch Detail
                      </Button>
                      {payrollExportError && <div className="text-xs text-destructive w-full">{payrollExportError}</div>}
                    </CardContent>
                    <div className="px-6 pb-4 text-[10px] text-muted-foreground">
                      Pay Week dropdown provides company bi-weekly periods with even pay-week numbers 02–52 (auto-fills dates + filename / Batch ID naming). Custom date ranges are fully supported. Filter by office. IST for India offices; HKT for Hong Kong offices.
                    </div>
                  </Card>
                )}
    </>
  );
}
